import React, { Component, Fragment } from 'react';
import { MutationFunction, QueryResult } from 'react-apollo';
import { pick } from 'lodash';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'recompose';
import { Form as FormikForm, Formik, FormikProps } from 'formik';
import { Divider } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';

import {
  GetGroupComponent,
  Group,
  GroupStatus,
  UpdateGroupComponent,
  UpdateGroupMutation,
} from 'api';
import { Dimensions, FormikEffect, Layouts, QueryManager } from 'components';
import {
  IWithGroupColors,
  IWithGroupModerators,
  IWithGroupSources,
  withGroupColors,
  withGroupModerators,
  withGroupSources,
} from 'providers/queries';
import { IWithNotify, IWithUser, withNotify, withUser } from 'providers';
import * as paths from 'paths';
import { isAdminUser } from 'helpers';
import { getGroupHexColor, isGroupOwner, isGroupOwnerOrModerator } from 'features/groups/helpers';
import { groupEdit } from 'apollo/reducers';
import { groups } from 'paths';

import { Actions, Header } from '../components/Edit';
import { FormLeft, FormRight } from '../components/FormShared';
import { GroupSchema } from '../validations';
import { IGroupForm, IMatchParams } from '../types';

import Controller from '../Controller';
import { Sources } from '../components/Sources';
import Moderators from '../components/Moderators';

interface IPropsEnhanced
  extends IWithUser,
    IWithNotify,
    IWithGroupColors,
    IWithGroupModerators,
    IWithGroupSources,
    WithStyles<typeof styles>,
    RouteComponentProps<IMatchParams> {}

interface IState {
  isWorking: boolean;
}

const styles = ({ spacing }: Theme) =>
  createStyles({
    divider: {
      marginTop: spacing(5),
      marginBottom: spacing(2),
    },
    dividerLast: {
      marginTop: spacing(2),
      marginBottom: spacing(2),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(
  withNotify,
  withUser,
  withRouter,
  withGroupColors,
  withGroupModerators,
  withGroupSources,
  withStyles(styles),
);

class EditGroup extends Component<IPropsEnhanced> {
  state: IState = {
    isWorking: false,
  };

  getInitialValues(group: Group): IGroupForm {
    return pick(
      group,
      'id',
      'title',
      'color',
      'icon_color',
      'icon',
      'image',
      'group_status',
      'content_approval_type',
      'allow_user_posts',
      'read_only',
      'department_id',
      'location_id',
      'is_private',
    );
  }

  getDividerColor(formikProps: FormikProps<IGroupForm>) {
    const { groupColors } = this.props;
    const { color } = formikProps.values;

    return getGroupHexColor(groupColors, color);
  }

  hasEditPermission(groupId: string) {
    const { user } = this.props;
    return isAdminUser(user) || isGroupOwnerOrModerator(user, groupId);
  }

  handleError = () => {
    const { notify } = this.props;
    this.setState({ isWorking: false });
    notify.mutationError();
  };

  handleCompleted = (data: UpdateGroupMutation) => {
    const { history, notify } = this.props;
    const { updateGroup } = data;
    const isDeleted = updateGroup.group_status === GroupStatus.Deleted;

    if (!isDeleted) {
      return this.setState({ isWorking: false });
    }

    notify.info({ i18n: { i18nKey: 'groups:deleteGroupNotify' } });
    history.push(groups);
  };

  handleDone = () => {
    this.props.history.goBack();
  };

  handleChange = (mutationFn: MutationFunction) => (values: IGroupForm) => {
    const { user } = this.props;

    this.setState({ isWorking: true });

    values.department_id = values.department_id ? values.department_id : null;
    values.location_id = values.location_id ? values.location_id : null;

    return mutationFn({
      variables: { data: values },
      update: (cache, result) => {
        groupEdit(cache, result, { currentUser: user });
      },
    });
  };

  renderMainLeft(formikProps: FormikProps<IGroupForm>) {
    const { classes, match } = this.props;
    const { group_id } = match.params;

    return (
      <Fragment>
        <FormLeft formikProps={formikProps} type="edit" />
        <Divider className={classes.divider} />
        <Moderators groupId={group_id} />
        <Divider className={classes.dividerLast} />
        <Sources groupId={group_id} />
      </Fragment>
    );
  }

  renderMainRight(formikProps: FormikProps<IGroupForm>) {
    const { groupColors, match, user } = this.props;
    const { group_id } = match.params;

    const { color } = formikProps.values;
    const showDelete = isGroupOwner(user, group_id);

    return (
      <Dimensions>
        {({ width }) => (
          <div style={{ width }}>
            <FormRight
              width={width}
              hexColor={getGroupHexColor(groupColors, color)}
              formikProps={formikProps}
            />
            <Actions
              hexColor={getGroupHexColor(groupColors, color)}
              formikProps={formikProps}
              showDelete={showDelete}
            />
          </div>
        )}
      </Dimensions>
    );
  }

  renderFormik(group: Group, mutationFn: MutationFunction) {
    const { isWorking } = this.state;
    const initialValues = this.getInitialValues(group);

    return (
      <Formik
        onSubmit={this.handleDone}
        initialValues={initialValues}
        validationSchema={GroupSchema()}>
        {(formikProps: FormikProps<IGroupForm>) => (
          <FormikForm>
            <FormikEffect delay={300} onChange={this.handleChange(mutationFn)} />
            <Layouts.App1Column
              desktopSize={12}
              main={
                <Layouts.AppMain2Column
                  dividerColor={this.getDividerColor(formikProps)}
                  header={<Header isWorking={isWorking} />}
                  left={this.renderMainLeft(formikProps)}
                  right={this.renderMainRight(formikProps)}
                />
              }
            />
          </FormikForm>
        )}
      </Formik>
    );
  }

  render() {
    const { match } = this.props;

    const { group_id } = match.params;
    const hasPermissions = this.hasEditPermission(group_id);

    if (!hasPermissions) {
      return <Redirect to={paths.groups} />;
    }

    return (
      <Controller>
        <UpdateGroupComponent onError={this.handleError} onCompleted={this.handleCompleted}>
          {mutationFn => (
            <GetGroupComponent variables={{ id: group_id }}>
              {(queryResult: QueryResult) => (
                <QueryManager {...queryResult}>
                  {({ data }) => {
                    const { getGroup } = data;
                    return this.renderFormik(getGroup, mutationFn);
                  }}
                </QueryManager>
              )}
            </GetGroupComponent>
          )}
        </UpdateGroupComponent>
      </Controller>
    );
  }
}

export default enhancers(EditGroup);
